/* eslint-disable max-lines */
import { IsoCountryCode } from '@transcend-io/privacy-types';

import type { SchemaToType } from '@main/schema-utils';
import { mkInput, mkType } from '@main/schema-utils';

import { DataSiloInterface } from './dataSiloPreview';
import { DataSubCategory } from './dataSubCategory';
import { DiscoClassScanConfig } from './discoClassScan';
import {
  ClassificationBrowseListQuickFilter,
  ClassificationPluginType,
  ClassificationSiloConnectionState,
  SimplifiedPluginStatus,
} from './enums';
import { Plugin } from './plugin';

export const ContentClassificationDataSiloFiltersInput = mkInput({
  name: 'ContentClassificationDataSiloFiltersInput',
  comment:
    'Inputs for determining what content classification data silos to filter',
  fields: {
    ids: {
      comment: 'Filter by data silo ids',
      type: 'id',
      modelName: 'dataSilo',
      optional: true,
      list: true,
    },
    text: {
      comment: 'Filter by text (title of data silo)',
      optional: true,
      type: 'string',
    },
    type: {
      comment: 'Filter by the data silo type',
      optional: true,
      list: true,
      type: 'string',
    },
    isDeleted: {
      comment: 'Filter for data silos that are deleted',
      optional: true,
      type: 'boolean',
    },
    pluginsEnabled: {
      comment: 'Filter by whether plugin(s) are enabled',
      optional: true,
      type: 'boolean',
    },
    owners: {
      comment: 'Filter by the assigned owner IDs',
      optional: true,
      type: 'id',
      modelName: 'user',
      list: true,
    },
    teams: {
      comment: 'Filter by the assigned team IDs',
      optional: true,
      type: 'id',
      modelName: 'team',
      list: true,
    },
    country: {
      comment: 'Filter by ISO country',
      type: { IsoCountryCode },
      optional: true,
      list: true,
    },
    countrySubDivision: {
      comment: 'Filter by ISO country subdivision',
      type: 'string',
      optional: true,
      list: true,
    },
    /** @deprecated Use `isRecurringScanEnabled` or dataSiloConnectionStates instead */
    // TODO: https://linear.app/transcend/issue/LINK-2382 - -Remove this field once we have removed the old Structured/Unstructured views
    simplifiedPluginStatus: {
      comment: 'Filter by simplified plugin status',
      optional: true,
      list: true,
      type: { SimplifiedPluginStatus },
    },
    classificationPluginTypes: {
      comment: 'Filter by classification plugin type',
      optional: true,
      list: true,
      type: { ClassificationPluginType },
    },
    hasSensitiveData: {
      comment: 'Filter by whether the data silo has any sensitive categories',
      optional: true,
      type: 'boolean',
    },
    isRecurringScanEnabled: {
      comment: 'Filter by whether the data silo has recurring scans enabled',
      optional: true,
      type: 'boolean',
    },
    dataSiloConnectionStates: {
      comment: 'Filter by the data silo connection state',
      optional: true,
      list: true,
      type: { ClassificationSiloConnectionState },
    },
    containsError: {
      comment: 'Filter by whether the data silo contains an error',
      optional: true,
      type: 'boolean',
    },
  },
});

/** Override type */
export type ContentClassificationDataSiloFiltersInput = SchemaToType<
  typeof ContentClassificationDataSiloFiltersInput
>;

// TODO: https://linear.app/transcend/issue/LINK-2550 - Remove two GQL queries contentClassificationSilos and unstructuredDiscoverySilos
export const ContentClassificationDataSilo = mkType({
  name: 'ContentClassificationDataSilo',
  comment: 'A data silo configured for content classification',
  interfaces: [DataSiloInterface],
  fields: {
    ...DataSiloInterface.fields,
    subDataPointCount: {
      comment: 'The number of subdatapoints this data silo has',
      type: 'int',
    },
    plugins: {
      comment: 'All plugins associated with this data silo',
      type: Plugin,
      list: true,
    },
    discoClassScanConfig: {
      comment:
        'Discovery and Classification scan configuration for this data silo',
      type: DiscoClassScanConfig,
      optional: true,
    },
  },
});

/** Override type */
export type ContentClassificationDataSilo = SchemaToType<
  typeof ContentClassificationDataSilo
>;

// TODO: https://linear.app/transcend/issue/LINK-2550 - Remove two GQL queries contentClassificationSilos and unstructuredDiscoverySilos
export const UnstructuredDiscoveryDataSilo = mkType({
  name: 'UnstructuredDiscoveryDataSilo',
  comment: 'A data silo configured for unstructured discovery',
  interfaces: [DataSiloInterface],
  fields: {
    ...DataSiloInterface.fields,
    plugins: {
      comment: 'All plugins associated with this data silo',
      type: Plugin,
      list: true,
    },
    scannedObjectCount: {
      comment: 'Count of files discovered by this plugin',
      type: 'int',
    },
    scannedVolumeSize: {
      comment: 'Total volume of data scanned by this plugin in bytes',
      type: 'string',
    },
  },
});

/** Override type */
export type UnstructuredDiscoveryDataSilo = SchemaToType<
  typeof UnstructuredDiscoveryDataSilo
>;

export const ClassificationDataSilo = mkType({
  name: 'ClassificationDataSilo',
  comment: 'A data silo configured for classification',
  interfaces: [DataSiloInterface],
  fields: {
    ...DataSiloInterface.fields,
    plugins: {
      comment: 'All plugins associated with this data silo',
      type: Plugin,
      list: true,
    },
    // Making following three fields optional because a silo might have structured or unstructured data but not both.
    dataPointCount: {
      comment: 'The number of dataPoints this data silo has',
      optional: true,
      type: 'int',
    },
    // TODO: https://linear.app/transcend/issue/LINK-2382 - Remove this field once we have migrated to the new filter
    simplifiedPluginStatus: {
      comment: 'The simplified classification plugin status of the data silo',
      type: { SimplifiedPluginStatus },
    },
    classifiedDataPointsCount: {
      comment:
        'Number of classified datapoints (auto-classified + user verified) in the data silo',
      type: 'int',
    },
    sensitiveDataPointsCount: {
      comment:
        'Number of sensitive datapoints (auto-classified + user verified) in the data silo',
      type: 'int',
    },
    scannedObjectCount: {
      comment: 'Count of files discovered by unstructured discovery plugin',
      optional: true,
      type: 'int',
    },
    classifiedScannedObjectCount: {
      comment: 'Number of files classified by unstructured discovery plugin',
      type: 'int',
    },
    sensitiveScannedObjectCount: {
      comment:
        'Number of files classified as sensitive by unstructured discovery plugin',
      type: 'int',
    },
    scannedVolumeSize: {
      comment:
        'Total volume of data scanned by unstructured discovery plugin in bytes',
      optional: true,
      type: 'string',
    },
    country: {
      comment: "A country's ISO code",
      optional: true,
      type: { IsoCountryCode },
    },
    countrySubDivision: {
      comment: 'A country subdivision ISO code',
      type: 'string',
      optional: true,
    },
    sensitiveCategories: {
      comment:
        'The data sub categories that have sensitive categories associated with this data silo',
      type: DataSubCategory,
      optional: true,
      list: true,
    },
    allDataSubCategories: {
      comment:
        'All data sub categories (both confirmed and guessed) associated with this data silo',
      type: DataSubCategory,
      optional: true,
      list: true,
    },
    // TODO: Update to `optional: false` once we have backfilled discoClassScanConfig for dataSilos
    discoClassScanConfig: {
      comment:
        'Discovery and Classification scan configuration for this data silo',
      type: DiscoClassScanConfig,
      optional: true,
    },
  },
});

/** Override type */
export type ClassificationDataSilo = SchemaToType<
  typeof ClassificationDataSilo
>;

export const ClassificationDataSiloQuickFilterCounts = mkType({
  name: 'ClassificationDataSiloQuickFilterCounts',
  comment: 'Counts for classification data silos filtered by quick filters',
  fields: {
    [ClassificationBrowseListQuickFilter.RecurringScansOn]: {
      comment: 'The number of recurring scans on data silos',
      type: 'int',
      optional: true,
    },
    [ClassificationBrowseListQuickFilter.RecurringScansOff]: {
      comment: 'The number of recurring scans off data silos',
      type: 'int',
      optional: true,
    },
    [ClassificationBrowseListQuickFilter.Disconnected]: {
      comment: 'The number of disconnected data silos',
      type: 'int',
      optional: true,
    },
    [ClassificationBrowseListQuickFilter.NotConfigured]: {
      comment: 'The number of data silos that are not configured',
      type: 'int',
      optional: true,
    },
    [ClassificationBrowseListQuickFilter.Error]: {
      comment: 'The number of data silos that have an error',
      type: 'int',
      optional: true,
    },
  },
});

/** Override type */
export type ClassificationDataSiloQuickFilterCounts = SchemaToType<
  typeof ClassificationDataSiloQuickFilterCounts
>;

export const ClassificationDataSiloSummaryStatistics = mkType({
  name: 'ClassificationDataSiloSummaryStatistics',
  comment: 'Summary statistics for classification data silos',
  fields: {
    totalDataSilos: {
      comment: 'Total number of data silos',
      type: 'int',
    },
    totalSensitiveStructuredDataSilos: {
      comment: 'Total number of structured data silos with sensitive data',
      type: 'int',
    },
    scanReadySilos: {
      comment: 'Total number of data silos that are ready for scanning',
      type: 'int',
    },
    nonReadySilos: {
      comment: 'Total number of data silos that are not ready for scanning',
      type: 'int',
    },
    totalSensitiveUnstructuredDataSilos: {
      comment: 'Total number of unstructured data silos with sensitive data',
      type: 'int',
    },
    totalDataPoints: {
      comment: 'Total number of data points',
      type: 'int',
    },
    totalClassifiedDataPoints: {
      comment: 'Total number of classified data points',
      type: 'int',
    },
    totalSensitiveDataPoints: {
      comment: 'Total number of sensitive data points',
      type: 'int',
    },
    totalScannedObjects: {
      comment: 'Total number of files',
      type: 'int',
    },
    totalClassifiedScannedObjects: {
      comment: 'Total number of classified files',
      type: 'int',
    },
    totalSensitiveScannedObjects: {
      comment: 'Total number of sensitive files',
      type: 'int',
    },
    totalDisconnectedDataSilos: {
      comment: 'Total number of disconnected data silos',
      type: 'int',
    },
    firstDisconnectedDataSilo: {
      comment: 'First disconnected data silo',
      type: ClassificationDataSilo,
      optional: true,
    },
  },
});

/** Override type */
export type ClassificationDataSiloSummaryStatistics = SchemaToType<
  typeof ClassificationDataSiloSummaryStatistics
>;

export const RestartSiloObjectClassificationsInput = mkInput({
  name: 'RestartSiloObjectClassificationsInput',
  comment:
    'Input for restarting the classification of the scanned objects of a data silo',
  fields: {
    dataSiloId: {
      comment: 'The id of the data silo',
      type: 'id',
      modelName: 'dataSilo',
    },
    clearExistingClassifications: {
      comment:
        'Whether to remove all existing classifications for scanned objects.',
      type: 'boolean',
      optional: true,
    },
  },
});

/** Override type */
export type RestartSiloObjectClassificationsInput = SchemaToType<
  typeof RestartSiloObjectClassificationsInput
>;

export const RestartSiloClassificationInput = mkInput({
  name: 'RestartSiloClassificationInput',
  comment:
    'Input for restarting the classification of the contents of a data silo',
  fields: {
    dataSiloId: {
      comment: 'The id of the data silo',
      type: 'id',
      modelName: 'dataSilo',
    },
    clearPendingGuesses: {
      comment:
        'Whether to remove unconfirmed guesses in addition to triggering a rescan. Defaults to true.',
      type: 'boolean',
      optional: true,
    },
  },
});

/** Override type */
export type RestartSiloClassificationInput = SchemaToType<
  typeof RestartSiloClassificationInput
>;
/* eslint-enable max-lines */
